import { TaskStatus } from '@dadohr/shared/domainTypes/experience';
import {
  IReportTaskInfo,
  IReportTaskInfoResolved,
  NumberType,
  ReportUser
} from '@dadohr/shared/domainTypes/reports';
import { ONE_DAY } from '@dadohr/shared/domainTypes/time';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Tooltip, Typography } from '@material-ui/core';
import { compact, orderBy, times, truncate } from 'lodash';
import { useMemo } from 'react';
import { WithShape } from '../../../../../components/Charts/Util';
import {
  ColoredChipCustom,
  getColor
} from '../../../../../components/ColoredChip';
import { DataTable2Row } from '../../../../../components/DataTable';
import { Loader } from '../../../../../components/Loader';
import { formatNumber } from '../../../../../components/Number';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../i18next';
import { Centered } from '../../../../../layout/Centered';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../layout/Flex';
import { toFullName, toSearchTermRegexp } from '../../../../../services/user';
import { FullTheme } from '../../../../../themes';
import { getTaskStatusChipConfig } from '../../../../Experiences/components/TaskStatusChip';
import { getItemDisplayName, getItemDisplayPair } from '../../../../Experiences/helpers/adminName';
import { ReportDrilldownSortKeys } from '../types';

export const TILE_SIZE = 36;
export const TILE_MARGIN = 2;
const SPACER_HEIGHT = 4;

export const SpacerTile = styled('div')((p) => ({
  height: SPACER_HEIGHT
}));

export const Tile = styled('div')((p) => ({
  width: TILE_SIZE,
  height: TILE_SIZE,
  borderRadius: p.theme.custom.border.radius(),
  boxSizing: 'border-box',
  margin: TILE_MARGIN,
  cursor: p.onClick ? 'pointer' : 'default'
}));

export const BlankTile = styled(Tile)((p) => ({
  backgroundColor: p.theme.custom.colors.gray.white
}));

export const Gap = styled('div')((p) => ({
  width: 8,
  height: TILE_SIZE + 1,
  margin: TILE_MARGIN
}));

export const AnchorContainer = styled('div')<{ isSpacer?: boolean }>((p) => ({
  position: 'relative',
  height: p.isSpacer ? SPACER_HEIGHT : TILE_SIZE + 1,
  width: 8,
  margin: TILE_MARGIN
}));

export const AnchorInner = styled('div')<{ isSpacer?: boolean }>((p) => ({
  position: 'absolute',
  top: -4,
  left: 3,
  background: p.theme.custom.colors.gray.w500,
  height: (p.isSpacer ? SPACER_HEIGHT : TILE_SIZE) + p.theme.spacing(1),
  width: 2
}));

export const AnchorTile = ({
  info,
  isSpacer
}: {
  info: IReportTaskInfo;
  isSpacer?: boolean;
}) => {
  return (
    <Tooltip title={getItemDisplayName(info)}>
      <AnchorContainer isSpacer={isSpacer}>
        <AnchorInner isSpacer={isSpacer} />
      </AnchorContainer>
    </Tooltip>
  );
};

export const WEEK_GAP_MOCK_ID_PREFIX = '$$$$$GAP$$$$$';
export const ANCHOR_GAP_MOCK_ID_PREFIX = `$$$$$ANCHOR$$$$$$`;

export const collectGapsAndSort = (
  tasks: IReportTaskInfo[],
  anchor: { name: string; position: number } | null,
  t: UnsafeTFunction
) => {
  if (tasks.length < 2) {
    return tasks;
  }
  const maxStart = Math.max(...tasks.map((t) => t.typicalStart / ONE_DAY)) || 0;
  const gaps = Math.ceil(maxStart / 7); // one per week
  const res = orderBy(
    compact([
      ...tasks,
      ...times(gaps).map<IReportTaskInfo | null>((i) => ({
        tplId: '',
        id: `${WEEK_GAP_MOCK_ID_PREFIX}`,
        title: `${t('Week ')} ${i + 1}`,
        typicalStart: i * 7 * ONE_DAY,
        actor: '',
        dependentOn: []
      })),
      anchor && {
        tplId: '',
        id: ANCHOR_GAP_MOCK_ID_PREFIX,
        title: `${anchor.name} • day ${
          Math.floor(anchor.position / ONE_DAY) + 1
        }`,
        typicalStart: anchor.position,
        actor: '',
        dependentOn: []
      }
    ]),
    [
      (t) => t.typicalStart,
      (t) =>
        t.id.startsWith(WEEK_GAP_MOCK_ID_PREFIX)
          ? 0
          : t.id.startsWith(ANCHOR_GAP_MOCK_ID_PREFIX)
          ? 1
          : 2
    ]
  );

  // Avoid doubling up a gap before the anchor gap and after
  return res.filter((x, i) => {
    if (x.id.startsWith(WEEK_GAP_MOCK_ID_PREFIX)) {
      const bef = res[i - 1];
      const aft = res[i + 1];
      if (
        bef?.id.startsWith(ANCHOR_GAP_MOCK_ID_PREFIX) ||
        aft?.id.startsWith(ANCHOR_GAP_MOCK_ID_PREFIX)
      ) {
        return false;
      }
    }
    return true;
  });
};

const StatusTileContainer = styled(Tile)<{
  status: TaskStatus | null;
}>((p) => {
  const { colors } = p.theme.custom;
  const getStatusColor = () => {
    switch (p.status) {
      case TaskStatus.LATE:
        return colors.red.w300;
      case TaskStatus.COMPLETE:
        return colors.green.w300;
      case TaskStatus.NOT_STARTED:
      case TaskStatus.RUNNING:
        return colors.gray.w200;
      case TaskStatus.SKIPPED:
      case TaskStatus.PAST_DEADLINE:
        return colors.gray.w300;
      default:
        return colors.gray.w100;
    }
  };

  return {
    backgroundColor: getStatusColor()
  };
});

const StatusTooltip = ({
  status,
  info,
  hideActor
}: {
  status: TaskStatus | null;
  info: IReportTaskInfoResolved;
  hideActor?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const [label, c] = status
    ? getTaskStatusChipConfig(status, t)
    : [t('Task does not apply'), 'gray' as const];
  return (
    <FlexContainerVertical>
      <div>
        {(() => {
          const { primary, secondary } = getItemDisplayPair(info);
          return (
            <>
              <div><strong>{primary}</strong></div>
              {secondary && <div><em>"{secondary}"</em></div>}
            </>
          );
        })()}
        <Typography variant="caption" color="inherit">
          <FlexContainer spacing={0.5}>
            {!hideActor && (
              <>
                <WithShape shape="circle" color={info.actor.color} small>
                  {info.actor.label}
                </WithShape>
                <div>•</div>
              </>
            )}
            <div>
              {info.dependentOn.length
                ? `${t('After completion of')}: ${info.dependentOn
                    .map((x) => getItemDisplayName(x))
                    .join(' & ')}`
                : `${t('Starts on day {{ count }}', {
                    count: Math.floor(info.typicalStart / ONE_DAY) + 1
                  })}`}
            </div>
          </FlexContainer>
        </Typography>
      </div>
      <div>
        <WithShape shape="square" color={getColor(theme, c)[1]}>
          {label}
        </WithShape>
      </div>
    </FlexContainerVertical>
  );
};

export const StatusTile = ({
  info,
  status,
  onClick,
  hideActor,
  onMouseEnter
}: {
  info: IReportTaskInfoResolved;
  status: TaskStatus | null;
  onClick?: (info: IReportTaskInfoResolved) => void;
  hideActor?: boolean;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
}) => {
  const _onClick = onClick ? () => onClick(info) : undefined;
  return (
    <Tooltip
      classes={{ tooltip: css({ '&&': { maxWidth: 'none' } }) }}
      title={
        <StatusTooltip info={info} status={status} hideActor={hideActor} />
      }
    >
      <StatusTileContainer
        status={status}
        onClick={_onClick}
        onMouseEnter={onMouseEnter}
      />
    </Tooltip>
  );
};

const getPercentageChipColors = (
  v: NumberType | null,
  theme: FullTheme
): [string, string, string] => {
  const cs = theme.custom.colors;
  if (v === null || (v.percentage === 0 && v.overduePercentage === 0)) {
    return ['white', cs.gray.w400, cs.gray.w400];
  }
  if (v.percentage === 1) {
    return ['white', cs.green.w500, cs.green.w500];
  }

  if (v.overduePercentage <= 0.3 && v.totalOverdue >= 3) {
    return ['white', cs.yellow.w600, cs.yellow.w600];
  }

  if (v.overduePercentage > 0.3 && v.totalOverdue >= 3) {
    return ['white', cs.red.w400, cs.red.w400];
  }

  return ['white', cs.green.w400, cs.green.w400];
};

const getPercentageChipColorsInverted = (
  v: NumberType | null,
  theme: FullTheme
): [string, string, string] => {
  const cs = theme.custom.colors;
  if (v === null || (v.percentage === 0 && v.overduePercentage === 0)) {
    return [cs.gray.w600, cs.gray.w100, cs.gray.w100];
  }
  if (v.percentage === 1) {
    return [cs.green.w500, cs.green.w100, cs.green.w100];
  }

  if (v.overduePercentage <= 0.3 && v.totalOverdue >= 3) {
    return [cs.yellow.w700, cs.yellow.w100, cs.yellow.w100];
  }

  if (v.overduePercentage > 0.3 && v.totalOverdue >= 3) {
    return [cs.red.w600, cs.red.w100, cs.red.w100];
  }

  // all cases shoud be handled at this point - we should never get here
  return [cs.green.w400, cs.green.w100, cs.green.w100];
};

export const CompletionPercentageChip = ({
  v,
  invertColors
}: {
  v: NumberType;
  invertColors?: boolean;
}) => {
  const theme = useTheme();

  const [color, backgroundColor, borderColor] = invertColors
    ? getPercentageChipColorsInverted(v, theme)
    : getPercentageChipColors(v, theme);

  return (
    <ColoredChipCustom
      color={color}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      width={60}
      size="small"
    >
      {formatNumber({ n: v.percentage, digits: 0, format: 'percent' })}
    </ColoredChipCustom>
  );
};

export const TableContainer = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`;

export const LEFT_COL_WIDTH = 34;

export const NameContainer = styled(FlexContainer)((p) => ({
  width: p.theme.spacing(LEFT_COL_WIDTH),
  cursor: p.onClick ? 'pointer' : 'default',
  minHeight: TILE_SIZE + TILE_MARGIN * 2
}));

export type DataTable2RowOtherData = {
  searchData: {
    name: string;
  };
  sortData: {
    completionPercentage: number | null;
    preferredName: string;
    lastName: string;
  };
};

export type RowData = DataTable2Row<DataTable2RowOtherData>;
export type SortFn = <X extends { otherData: DataTable2RowOtherData }>(
  rows: X[]
) => X[];

export const SORTERS: {
  [key in ReportDrilldownSortKeys]: SortFn;
} = {
  [ReportDrilldownSortKeys.COMPLETION_RATE_ASC]: (rows) => {
    return orderBy(rows, [
      (r) => {
        const { completionPercentage } = r.otherData.sortData;
        // when nothing is required, put them at the end of the list - in either direction maybe
        return completionPercentage === null ? 2 : completionPercentage;
      },
      (r) => r.otherData.sortData.lastName || 'zzzzzzzzzzzz',
      (r) => r.otherData.sortData.preferredName || 'zzzzzzzzzzzz'
    ]);
  },
  [ReportDrilldownSortKeys.COMPLETION_RATE_DESC]: (rows) => {
    return orderBy(rows, [
      (r) => {
        const { completionPercentage } = r.otherData.sortData;
        // when nothing is required, put them at the end of the list - in either direction maybe
        return completionPercentage === null ? 2 : -completionPercentage;
      },
      (r) => r.otherData.sortData.lastName || 'zzzzzzzzzzzz',
      (r) => r.otherData.sortData.preferredName || 'zzzzzzzzzzzz'
    ]);
  },

  [ReportDrilldownSortKeys.NAME_ASC]: (rows) => {
    return orderBy(rows, [
      (r) => r.otherData.sortData.lastName || 'zzzzzzzzzzzz',
      (r) => r.otherData.sortData.preferredName || 'zzzzzzzzzzzz',
      (r) => {
        const { completionPercentage } = r.otherData.sortData;
        // when nothing is required, put them at the end of the list - in either direction maybe
        return completionPercentage === null ? 2 : completionPercentage;
      }
    ]);
  },
  [ReportDrilldownSortKeys.NAME_DESC]: (rows) => {
    return orderBy(
      rows,
      [
        (r) => r.otherData.sortData.lastName || 'AAAAAA',
        (r) => r.otherData.sortData.preferredName || 'AAAAA',
        (r) => {
          const { completionPercentage } = r.otherData.sortData;
          // when nothing is required, put them at the end of the list - in either direction maybe
          return completionPercentage === null ? 2 : completionPercentage;
        }
      ],
      ['desc', 'desc', 'asc']
    );
  }
  // sort by instance start date? ---> needs to be added to backend too
};

export const shortName = (u: ReportUser, threshold = 20) => {
  let fullName = toFullName(u);
  if (fullName.length > threshold) {
    const shorterName = toFullName({
      preferredName: `${u.preferredName[0]}.`,
      lastName: u.lastName
    });
    return truncate(shorterName, {
      length: threshold,
      omission: '…'
    });
  }
  return fullName;
};

export const shortenLabel = (label: string, threshold = 14) => {
  if (label.length > threshold && label.includes(' ')) {
    const parts = label.split(/\s|-/);
    // 2nd Regional director -> 2RD
    // HR Business Parter -> HRBP
    return parts
      .map((p) => (p.match(/^([A-Z]+)/)?.[1] || p[0] || '').toUpperCase())
      .join('');
  }
  return label;
};

export const WithTooltipIfTruncated = ({
  short,
  full
}: {
  short: string;
  full: string;
}) => {
  return (
    <Tooltip title={short.length < full.length ? full : ''}>
      <span>{short}</span>
    </Tooltip>
  );
};

export const useApplySearchAndSortRows = (
  allRows: RowData[] | null,
  search: string,
  sort: ReportDrilldownSortKeys
) => {
  const filteredRows = useMemo<null | RowData[]>(() => {
    if (!allRows || !search) {
      return allRows;
    }
    const searchRe = toSearchTermRegexp(search);
    return allRows.filter(
      (r) => !!searchRe.exec(r.otherData.searchData.name || '')
    );
  }, [allRows, search]);

  return useMemo<null | RowData[]>(() => {
    if (!filteredRows) {
      return null;
    }
    const sorter =
      SORTERS[sort] || SORTERS[ReportDrilldownSortKeys.COMPLETION_RATE_ASC];
    return sorter(filteredRows);
  }, [filteredRows, sort]);
};

export const ReportDrilldownLoader = () => <Loader height={500} />;
export const ReportDrilldownEmptyState = () => {
  const { t } = useUnsafeTranslation();
  return (
    <Centered height={200}>
      <Typography variant="caption">
        {t('There is no data for this report')}
      </Typography>
    </Centered>
  );
};
export const ReportDrilldownError = () => {
  const { t } = useUnsafeTranslation();
  return (
    <Centered height={200}>
      <Typography variant="caption">{t('Failed to load data')}</Typography>
    </Centered>
  );
};

export const gapOrAnchorOrOther = (
  t: IReportTaskInfo,
  toOther: () => React.ReactNode
): React.ReactNode => {
  if (t.id.startsWith(ANCHOR_GAP_MOCK_ID_PREFIX)) {
    return <AnchorTile info={t} />;
  }
  if (t.id.startsWith(WEEK_GAP_MOCK_ID_PREFIX)) {
    return <Gap />;
  }
  return toOther();
};

export type TrackingFns = {
  trackBlockClick: (
    tsk: IReportTaskInfoResolved,
    status: string,
    type: 'status' | 'aggregatedStatus'
  ) => void;
  trackBlockHover: (
    tsk: IReportTaskInfoResolved,
    status: string,
    type: 'status' | 'aggregatedStatus'
  ) => void;
  trackRowExpanded: (tplId: string, rowName: string) => void;
  openInstanceDialog: (instanceId: string) => void;
  openExecutionDialog: (tsk: IReportTaskInfoResolved) => void;
};

export const taskStatusToString = (
  status: TaskStatus | null,
  t: UnsafeTFunction
) => {
  return status ? getTaskStatusChipConfig(status, t)[0] || status : 'n/a';
};
